import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

/** Estado del tooltip para que el disparador pueda abrirlo con un toque (táctil no tiene hover). */
const TooltipEstado = React.createContext<{ open: boolean; setOpen: (v: boolean) => void } | null>(null);

/** Radix Root con el estado expuesto al disparador; acepta `open`/`onOpenChange` igual que Radix. */
function Tooltip({ open: openProp, defaultOpen, onOpenChange, ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [abiertoLocal, setAbiertoLocal] = React.useState(defaultOpen ?? false);
  const controlado = openProp !== undefined;
  const open = controlado ? openProp : abiertoLocal;
  const setOpen = React.useCallback((v: boolean) => {
    if (!controlado) setAbiertoLocal(v);
    onOpenChange?.(v);
  }, [controlado, onOpenChange]);
  const estado = React.useMemo(() => ({ open, setOpen }), [open, setOpen]);
  return (
    <TooltipEstado.Provider value={estado}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </TooltipEstado.Provider>
  );
}

/**
 * Disparador. El tooltip sale con hover (y con un toque si `abrirAlTocar`), nunca con el foco: al
 * abrir un popover o un modal el foco cae en su primer elemento y Radix abría ese tooltip solo.
 */
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> & {
    /** En táctil, un toque abre y otro cierra (para explicaciones, no para botones de acción). */
    abrirAlTocar?: boolean;
  }
>(({ abrirAlTocar = false, onFocus, onPointerDown, onClick, ...props }, ref) => {
  const estado = React.useContext(TooltipEstado);
  const tactil = React.useRef(false);
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      // preventDefault salta el handler de Radix que abre al enfocar (composeEventHandlers).
      onFocus={(e) => { onFocus?.(e); e.preventDefault(); }}
      onPointerDown={(e) => { onPointerDown?.(e); tactil.current = e.pointerType !== "mouse"; }}
      onClick={(e) => {
        onClick?.(e);
        if (!abrirAlTocar || !tactil.current || !estado) return;
        // Radix cierra en el click; el cambio va después para que no lo pise.
        const siguiente = !estado.open;
        window.setTimeout(() => estado.setOpen(siguiente), 0);
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  // Portal: el contenido se monta en el body, así no lo recorta ningún ancestro con
  // `overflow-hidden` (tarjetas, contenedores de tabla, modales…).
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
